import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { ApiUser } from '../types/api';
import * as authService from '../services/authService';
import { getAccessToken } from '../services/apiClient';
import { logger } from '../lib/logger';

const log = logger.createNamespace('AuthContext');

interface AuthContextType {
  user: ApiUser | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (
    email: string,
    password: string,
    fullName: string
  ) => Promise<{ error: Error | null }>;
  signOut: () => Promise<{ error: Error | null }>;
  resetPassword: (email: string) => Promise<{ error: Error | null }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<ApiUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const token = getAccessToken();
        if (token) {
          const currentUser = await authService.getCurrentUser();
          setUser(currentUser);
          log.debug('Auth initialized', { hasUser: !!currentUser });
        } else {
          log.debug('Auth initialized', { hasUser: false });
        }
      } catch (error) {
        log.error('Failed to initialize auth', error);
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    try {
      log.debug('Attempting sign in', { email });
      const result = await authService.login(email, password);
      if (result.error) {
        log.warn('Sign in failed', { message: result.error.message });
        return { error: result.error };
      }
      setUser(result.user);
      log.info('Sign in successful');
      return { error: null };
    } catch (error) {
      log.error('Sign in exception', error);
      return { error: error as Error };
    }
  }, []);

  const signUp = useCallback(
    async (email: string, password: string, fullName: string) => {
      try {
        log.debug('Attempting sign up', { email });
        const result = await authService.register(email, password, fullName);
        if (result.error) {
          log.warn('Sign up failed', { message: result.error.message });
          return { error: result.error };
        }
        setUser(result.user);
        log.info('Sign up successful');
        return { error: null };
      } catch (error) {
        log.error('Sign up exception', error);
        return { error: error as Error };
      }
    },
    []
  );

  const signOut = useCallback(async () => {
    try {
      log.debug('Signing out');
      await authService.logout();
      setUser(null);
      log.info('Sign out successful');
      return { error: null };
    } catch (error) {
      log.error('Sign out exception', error);
      return { error: error as Error };
    }
  }, []);

  const resetPassword = useCallback(async (_email: string) => {
    // Password reset via API not yet implemented; placeholder
    return { error: new Error('Password reset not implemented') };
  }, []);

  const value: AuthContextType = {
    user,
    loading,
    signIn,
    signUp,
    signOut,
    resetPassword,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
